import React, { useState, useEffect } from 'react';
import { Users, Plus, AlertCircle, Loader, ChevronDown, ChevronUp, X } from 'lucide-react';
import CollaboratorItem from './CollaboratorItem';
import { useNotifications } from '../../contexts/NotificationContext';

interface Collaborator {
  id: number;
  userId: number;
  userName: string;
  userEmail: string;
  role: 'collaborator' | 'viewer';
  addedAt: string;
}

interface AvailableUser {
  id: number;
  name: string;
  email: string;
}

interface SessionCollaboratorsListProps {
  sessionId: number | null;
  isOwner: boolean;
  getCollaborators: (sessionId: number) => Promise<{ data: Collaborator[] }>;
  getAvailable: (sessionId: number) => Promise<{ data: AvailableUser[] }>;
  getAllUsers: () => Promise<{ data: AvailableUser[] }>;
  addCollaborator: (sessionId: number, userId: number) => Promise<{ data: Collaborator }>;
  removeCollaborator: (sessionId: number, userId: number) => Promise<unknown>;
  onError?: (message: string) => void;
  // Fires after a collaborator is successfully added or removed. The parent's
  // session list (with its own "collaborator_names" / "shared" badge, fetched
  // separately from this component's own collaborator state) has no other way
  // to know a change just happened — without this it stays stale until the
  // next full page load or manual History-panel reopen.
  onCollaboratorsChanged?: () => void;
  // 'panel' (default) renders the original full-width card used by AI Council.
  // 'dropdown' renders a compact pill button (matches New Session / History
  // styling) that opens the same list + Add/Remove UI in a floating popover —
  // used by Jarvis Intelligence's header to avoid a full-width standalone block.
  variant?: 'panel' | 'dropdown';
  // Tailwind color name for the dropdown variant's active/hover state, matched
  // to each page's existing History-button accent (Intelligence = indigo,
  // AI Council = purple) so Collaborators doesn't clash with its neighbors.
  accentColor?: 'indigo' | 'purple';
}

// De-duplicate a collaborators array by userId, keeping the first (lowest id) entry.
// Guards against the race where fetchCollaborators and flushPending both resolve
// and append the same user concurrently.
const dedupeByUserId = (list: Collaborator[]): Collaborator[] => {
  const seen = new Set<number>();
  return list.filter(c => {
    if (seen.has(c.userId)) return false;
    seen.add(c.userId);
    return true;
  });
};

// Tailwind needs literal class names (not string-interpolated) to survive
// purging, so the accent options are spelled out per color rather than built
// from a template string.
const DROPDOWN_ACCENTS = {
  indigo: { active: 'bg-indigo-100 border-indigo-300 text-indigo-700', hover: 'hover:border-indigo-300 hover:text-indigo-700', badge: 'bg-electric-blue' },
  purple: { active: 'bg-purple-100 border-purple-300 text-purple-700', hover: 'hover:border-purple-300 hover:text-purple-700', badge: 'bg-purple-600' },
} as const;

const SessionCollaboratorsList: React.FC<SessionCollaboratorsListProps> = ({
  sessionId,
  isOwner,
  getCollaborators,
  getAvailable,
  getAllUsers,
  addCollaborator,
  removeCollaborator,
  onError,
  onCollaboratorsChanged,
  variant = 'panel',
  accentColor = 'indigo',
}) => {
  const [collaborators, setCollaborators] = useState<Collaborator[]>([]);
  const [loading, setLoading] = useState(false);
  // Dropdown variant starts fully closed (no inline list); panel variant keeps
  // its original collapsed-card default.
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [availableUsers, setAvailableUsers] = useState<AvailableUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [isRemoving, setIsRemoving] = useState<number | null>(null);

  // Remove-confirmation modal state (replaces window.confirm with in-app UI)
  const [removeTarget, setRemoveTarget] = useState<Collaborator | null>(null);

  useEffect(() => {
    if (!sessionId) {
      // Session was cleared (new session screen or navigation away) — reset list
      setCollaborators([]);
      setLoading(false);
      return;
    }
    fetchCollaborators();
  }, [sessionId]);

  // Re-fetch when another collaborator/the owner changes this session's
  // membership from elsewhere — without this, an already-open panel keeps
  // showing a stale list/count until it's closed and reopened.
  const { subscribeToLiveNotifications } = useNotifications();
  useEffect(() => {
    if (!sessionId) return;
    return subscribeToLiveNotifications(() => fetchCollaborators());
  }, [sessionId, subscribeToLiveNotifications]); // eslint-disable-line

  const fetchCollaborators = async () => {
    if (!sessionId) return;
    try {
      setLoading(true);
      setError(null);
      const res = await getCollaborators(sessionId);
      setCollaborators(dedupeByUserId(res.data || []));
    } catch (err: any) {
      const msg = err.response?.data?.error || 'Failed to load collaborators';
      setError(msg);
      onError?.(msg);
    } finally {
      setLoading(false);
    }
  };

  const openModal = async () => {
    if (!sessionId) return; // guard: no session yet
    setIsModalOpen(true);
    setSelectedUserId(null);
    setSearchTerm('');
    setLoadingUsers(true);
    try {
      const res = await getAvailable(sessionId);
      setAvailableUsers(res.data || []);
    } catch (err: any) {
      setAvailableUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleAdd = async () => {
    if (!selectedUserId || !sessionId) return;

    try {
      setIsAdding(true);
      setError(null);
      const res = await addCollaborator(sessionId, selectedUserId);
      if (res.data) setCollaborators(prev => dedupeByUserId([...prev, res.data]));
      setIsModalOpen(false);
      onCollaboratorsChanged?.();
    } catch (err: any) {
      const msg = err.response?.data?.error || 'Failed to add collaborator';
      setError(msg);
      onError?.(msg);
    } finally {
      setIsAdding(false);
    }
  };

  const handleRemove = (collaboratorId: number) => {
    const c = collaborators.find(x => x.id === collaboratorId);
    if (!c) return;
    setRemoveTarget(c);
  };

  const confirmRemove = async () => {
    const c = removeTarget;
    if (!c || !sessionId) { setRemoveTarget(null); return; }
    try {
      setIsRemoving(c.id);
      setError(null);
      await removeCollaborator(sessionId, c.userId);
      setCollaborators(prev => prev.filter(x => x.id !== c.id));
      onCollaboratorsChanged?.();
    } catch (err: any) {
      const msg = err.response?.data?.error || 'Failed to remove collaborator';
      setError(msg);
      onError?.(msg);
    } finally {
      setIsRemoving(null);
      setRemoveTarget(null);
    }
  };

  const filteredUsers = availableUsers.filter(
    u =>
      u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Shared list body (error / empty-no-session / loading / empty / list) — used by
  // the full panel variant. compact=true (dropdown variant) swaps CollaboratorItem's
  // wide-panel row (avatar + name/email + role badge + date + remove, all inline)
  // for a two-line compact row — the wide row's horizontal layout doesn't fit in a
  // 320px popover without the role badge and date visually colliding with the name.
  const renderListBody = (compact = false) => (
    <>
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {!sessionId ? (
        <div className="text-center py-8">
          <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">Submit a prompt to start a session</p>
          {isOwner && (
            <p className="text-gray-400 text-xs mt-1">Collaborators can be added once the session is created</p>
          )}
        </div>
      ) : loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader className="w-5 h-5 text-blue-600 animate-spin" />
        </div>
      ) : collaborators.length === 0 ? (
        <div className="text-center py-8">
          <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">No collaborators yet</p>
          {isOwner && (
            <p className="text-gray-400 text-xs mt-1">Add collaborators to work together on this session</p>
          )}
        </div>
      ) : compact ? (
        <div className="space-y-1">
          {collaborators.map(c => (
            <div key={c.id} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-50 transition-colors group">
              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
                {c.userName.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-900 truncate">{c.userName}</div>
                <div className="text-xs text-gray-500 truncate">{c.userEmail}</div>
              </div>
              {isOwner && (
                <button
                  onClick={() => handleRemove(c.id)}
                  disabled={isRemoving === c.id}
                  className="p-1 text-gray-300 hover:text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0 opacity-0 group-hover:opacity-100"
                  title="Remove collaborator"
                >
                  {isRemoving === c.id ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />}
                </button>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-2">
          {collaborators.map(c => (
            <CollaboratorItem
              key={c.id}
              id={c.id}
              userName={c.userName}
              userEmail={c.userEmail}
              role={c.role}
              addedAt={c.addedAt}
              isOwner={!isOwner}
              onRemove={handleRemove}
              isRemoving={isRemoving === c.id}
            />
          ))}
        </div>
      )}
    </>
  );

  // Shared Add/Remove modals — identical for both variants.
  const modals = (
    <>
      {isModalOpen && sessionId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-96 max-h-96 flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Add Collaborator</h3>
              <button onClick={() => setIsModalOpen(false)} className="p-1 hover:bg-gray-100 rounded transition-colors">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 scrollbar-hide">
              {loadingUsers ? (
                <div className="flex items-center justify-center py-8">
                  <Loader className="w-5 h-5 text-blue-600 animate-spin" />
                </div>
              ) : availableUsers.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-500 text-sm">No users available to add</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <input
                    type="text"
                    placeholder="Search by name or email..."
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <div className="space-y-2 max-h-60 overflow-y-auto scrollbar-hide">
                    {filteredUsers.map(user => (
                      <label
                        key={user.id}
                        className="flex items-center p-2 hover:bg-gray-50 rounded cursor-pointer transition-colors"
                      >
                        <input
                          type="radio"
                          name="collab-user"
                          value={user.id}
                          checked={selectedUserId === user.id}
                          onChange={() => setSelectedUserId(user.id)}
                          className="w-4 h-4 text-blue-600"
                        />
                        <div className="ml-3 flex-1 min-w-0">
                          <div className="font-medium text-sm text-gray-900">{user.name}</div>
                          <div className="text-xs text-gray-500 truncate">{user.email}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-200">
              <button
                onClick={() => setIsModalOpen(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAdd}
                disabled={!selectedUserId || isAdding || loadingUsers}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isAdding ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    Adding...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4" />
                    Add Collaborator
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {removeTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-96 flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Remove Collaborator</h3>
              <button onClick={() => setRemoveTarget(null)} className="p-1 hover:bg-gray-100 rounded transition-colors">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-4">
              <p className="text-sm text-gray-600">
                Remove <span className="font-medium text-gray-900">{removeTarget.userName}</span> as a collaborator on this session? They will lose access immediately.
              </p>
            </div>
            <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-200">
              <button
                onClick={() => setRemoveTarget(null)}
                disabled={isRemoving === removeTarget.id}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={confirmRemove}
                disabled={isRemoving === removeTarget.id}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isRemoving === removeTarget.id ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    Removing...
                  </>
                ) : (
                  'Remove'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );

  // Compact dropdown variant — a pill button (matching New Session / History
  // styling) that opens a floating popover with the same list + Add/Remove UI.
  // Used by Jarvis Intelligence's header so Collaborators no longer takes a
  // full-width row of its own.
  if (variant === 'dropdown') {
    const accent = DROPDOWN_ACCENTS[accentColor];
    return (
      <div className="relative">
        <button
          onClick={() => setIsDropdownOpen(p => !p)}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border transition-all whitespace-nowrap ${
            isDropdownOpen ? accent.active : `bg-white border-gray-200 text-gray-600 ${accent.hover}`
          }`}
        >
          <Users className="w-4 h-4" />
          Collaborators
          {collaborators.length > 0 && (
            <span className={`${accent.badge} text-white text-xs px-1.5 py-0.5 rounded-full`}>{collaborators.length}</span>
          )}
          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
        </button>

        {isDropdownOpen && (
          <>
            {/* Click-outside overlay */}
            <div className="fixed inset-0 z-30" onClick={() => setIsDropdownOpen(false)} />
            {/* Anchored to the trigger's left edge (the button sits mid-header, not
                flush with the viewport's right edge, so right-anchoring pushed the
                popover past the screen edge). max-w caps it on narrow viewports;
                max-h caps the popover itself with the list scrolling independently
                so the Add button/header stay visible when there are many collaborators. */}
            <div className="absolute left-0 top-full mt-2 w-80 max-w-[calc(100vw-2rem)] max-h-96 bg-white rounded-lg border border-gray-200 shadow-lg z-40 p-4 flex flex-col">
              <div className="flex items-center justify-between mb-3 flex-shrink-0">
                <h3 className="text-sm font-semibold text-gray-900">Collaborators</h3>
                {isOwner && (
                  <button
                    onClick={openModal}
                    disabled={!sessionId}
                    title={!sessionId ? 'Submit a prompt first to create a session, then add collaborators' : undefined}
                    className="flex items-center gap-1 px-2 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-xs font-medium disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Add
                  </button>
                )}
              </div>
              {/* min-h-0 is load-bearing here: a flex child won't shrink below its
                  content size by default, which silently defeats overflow-y-auto
                  and lets the list blow past the popover's max-h instead of scrolling. */}
              <div className="overflow-y-auto scrollbar-hide min-h-0">
                {renderListBody(true)}
              </div>
            </div>
          </>
        )}

        {modals}
      </div>
    );
  }

  return (
    <>
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-900">
              Collaborators
              {collaborators.length > 0 && (
                <span className="ml-2 text-sm font-normal text-gray-500">({collaborators.length})</span>
              )}
            </h3>
          </div>
          <div className="flex items-center gap-2">
            {isOwner && (
              <button
                onClick={openModal}
                disabled={!sessionId}
                title={!sessionId ? 'Submit a prompt first to create a session, then add collaborators' : undefined}
                className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Plus className="w-4 h-4" />
                Add Collaborator
              </button>
            )}
            <button
              onClick={() => setIsCollapsed(p => !p)}
              className="p-1 hover:bg-gray-100 rounded transition-colors"
              title={isCollapsed ? 'Expand' : 'Collapse'}
            >
              {isCollapsed ? (
                <ChevronDown className="w-5 h-5 text-gray-600" />
              ) : (
                <ChevronUp className="w-5 h-5 text-gray-600" />
              )}
            </button>
          </div>
        </div>

        {!isCollapsed && renderListBody()}
      </div>

      {modals}
    </>
  );
};

export default SessionCollaboratorsList;
